<template>
  <Menu
    as="div"
    class="relative ml-3"
  >
    <MenuButton
      class="flex items-center max-w-xs text-sm bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 lg:p-2 lg:rounded-md lg:hover:bg-gray-50"
    >
      <div class="relative">
        <ld-icon
          icon="UserIcon"
          class="w-6 h-6 text-gray-400 fill-current"
        />
        <span
          class="absolute bottom-0 right-0 block transform translate-x-1/2 translate-y-1/2 border-2 border-white rounded-full"
        >
          <span
            class="block h-1.5 w-1.5 rounded-full"
            :class="statusClass"
          />
        </span>
      </div>
      <span class="text-sm font-medium text-gray-700 lg:block"><span class="sr-only">Open user menu for </span><span class="capitalize">{{ user?.attributes?.username }}</span></span>
      <ld-icon
        class="flex-shrink-0 w-5 h-5 ml-1 text-gray-400 lg:block"
        icon="ChevronDownIcon"
        aria-hidden="true"
      />
    </MenuButton>
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <MenuItems
        class="absolute right-0 w-48 py-1 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none"
      >
        <MenuItem disabled>
          <div
            class="block w-full px-4 py-2 text-sm font-medium text-center text-gray-400 capitalize"
          >
            {{ status }} ({{ duration }})
          </div>
        </MenuItem>
        <MenuItem
          v-if="pauseCodes.length === 0"
          v-slot="{ active }"
        >
          <button
            :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 w-full']"
            @click="pause()"
          >
            Pause
          </button>
        </MenuItem>
        <template v-if="!auth.pause">
          <MenuItem
            v-for="pauseCode in pauseCodes"
            :key="pauseCode.id"
            v-slot="{ active }"
          >
            <button
              :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 w-full']"
              @click="pause(pauseCode)"
            >
              {{ pauseCode.attributes.name }}
            </button>
          </MenuItem>
          <MenuItem v-if="auth.organizations.length > 1">
            <button
              :class="['block px-4 py-2 text-sm text-gray-700 w-full flex']"
              @click="auth.setSelectedOrganization(undefined)"
            >
              <ld-icon
                icon="OfficeBuildingIcon"
                class="w-4 h-4 text-gray-500"
                aria-hidden="true"
              />
              Change Organization
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <router-link
              :to="{ name: 'AgentProfile' }"
              :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 w-full flex items-center']"
            >
              <ld-icon
                icon="UserIcon"
                class="w-4 h-4 mr-2 text-gray-500"
                aria-hidden="true"
              />
              Agent Profile
            </router-link>
          </MenuItem>
          <MenuItem
            v-if="$can('list', 'Token')"
            v-slot="{ active }"
          >
            <router-link
              :to="{ name: 'TokensList' }"
              :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 w-full']"
            >
              Manage API Tokens
            </router-link>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button
              :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 w-full']"
              @click="logout"
            >
              Logout
            </button>
          </MenuItem>
        </template>
        <MenuItem v-else>
          <button
            :class="['bg-gray-100', 'block px-4 py-2 text-sm text-gray-700 w-full']"
            @click="resume()"
          >
            Resume
          </button>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
  <PausedModal
    :open="status === 'PAUSED' && (auth.pause?.attributes.isAdminPause ?? false) === false"
  />
</template>

<script setup lang="ts">
  import { computed, defineComponent, onMounted, onUnmounted, Ref, ref, watch } from 'vue'
  import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
  import { useAuthStore } from '/@/store/authStore'
  import useLetsDialSocket from '/@/services/letsDialSocket/useLetsDialSocket'
  import PausedModal from '/@/components/ui/modals/PausedModal.vue'
  import { PauseCode } from '@letsdial/api/src/lib/types/PauseCode'
  import useLetsDialAPI from '/@/services/letsDialAPI/useLetsDialAPI'
  import { DateTime } from 'luxon'

  const api = useLetsDialAPI()
  const auth = useAuthStore()
  const { socket } = useLetsDialSocket()
  const duration = ref('')
  const pauseCodes: Ref<Array<PauseCode>> = ref([])
  let timeInterval: any
  const statusClass = computed(() => {
    switch (auth.currentStatus) {
      case 'IDLE':
        return 'bg-teal-300'
      default:
        return 'bg-gray-300'
    }
  })
  const user = computed(() => {
    return auth.user
  })
  const status = computed(() => {
    return auth.currentStatus
  })
  const logout = async () => {
    await auth.logout()
  }
  watch(auth, async (store) => {
    if (store.selectedOrganization?.id) {
      pauseCodes.value =
        (
          await api.pauseCodes.list({
            filter: { organization: { id: store.selectedOrganization.id } },
          })
        )?.data ?? []
    }
  })
  onMounted(async () => {
    if (auth.selectedOrganization?.id) {
      pauseCodes.value =
        (
          await api.pauseCodes.list({
            filter: { organization: { id: auth.selectedOrganization?.id } },
          })
        )?.data ?? []
    }
    timeInterval = setInterval(() => {
      if (auth.statusChangeAt) {
        const diff = DateTime.now().diff(auth.statusChangeAt)
        duration.value = diff.toFormat('hh:mm:ss')
      }
    }, 1000)
  })
  onUnmounted(() => {
    clearInterval(timeInterval)
  })
  const pause = (pause?: PauseCode) => {
    auth.setPause(pause)
    auth.setAvailabilityStatus('PAUSED')
  }
  const resume = () => {
    auth.setPause(undefined)
    auth.setAvailabilityStatus('IDLE', true)
  }
</script>
